import { useSuspenseQuery } from '@apollo/client/react';
import { GET_PARTNER_MAPPING } from '@optools/services/operator-gateway/queries';
import { Channel, TargetView } from '@optools/services/operator-gateway/types/generated/graphql';
import { useMemo } from 'react';

import { createTargetId } from '../utils/targets';

export type PatchedChannel = Pick<Channel, 'id' | 'name'>;

export const usePartnerData = () => {
  const { data } = useSuspenseQuery(GET_PARTNER_MAPPING);

  const dataCenters = useMemo(
    () => data?.getAllChannels?.toSorted((a, b) => a.name.localeCompare(b.name)) ?? [],
    [data?.getAllChannels],
  );

  const partners = useMemo(
    () => data?.getAllTargets?.toSorted((a, b) => a.name.localeCompare(b.name)) ?? [],
    [data?.getAllTargets],
  );

  const channelMap = useMemo(() => {
    const map = new Map<number, PatchedChannel>();
    dataCenters
      .flatMap(({ channels }) => channels)
      .forEach((channel) => map.set(channel.id, channel));
    return map;
  }, [dataCenters]);

  const targetMap = useMemo(() => {
    const map = new Map<string, TargetView>();
    partners
      .flatMap(({ targets }) => targets)
      .forEach((target) => {
        const key = createTargetId(target);
        map.set(key, target);
      });
    return map;
  }, [partners]);

  const channels = useMemo(() => [...channelMap.values()], [channelMap]);
  const targets = useMemo(() => [...targetMap.values()], [targetMap]);

  return { channelMap, channels, dataCenters, partners, targetMap, targets };
};
